import React, { useState, useRef } from 'react';
import { Search, Building, MapPin, Phone, Mail, ExternalLink, AlertCircle, CheckCircle, ArrowRight, Users, PieChart, FileText, Shield } from 'lucide-react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';

import API_BASE_URL from '../config/api';
const API = API_BASE_URL;

const AvoirsLPP = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const signatureRef = useRef(null);
  const sectionRef = useRef(null); // Référence à la section du formulaire
  const [formData, setFormData] = useState({
    // Étape 1
    firstName: '',
    lastName: '',
    email: '',
    telephone: '',
    conseiller_recommandant: '',
    dateOfBirth: '',
    gender: '',
    
    // Étape 2
    address: '',
    postalCode: '',
    city: '',
    avsNumber: '',
    formerEmployers: '',
    
    // Étape 3
    motif: '',
    signatureLocation: '',
    signatureDate: new Date().toISOString().split('T')[0], // Date du jour par défaut
    signatureImage: '', // Signature dessinée en base64
    signature: false,
    acceptTerms: false,
    consentRGPD: false // Consentement RGPD pour données sensibles
  });

  const statistics = [
    {
      icon: PieChart,
      value: "5.6 Milliards CHF",
      label: "d'avoirs égarés en Suisse",
      color: "from-blue-400 to-blue-600"
    },
    {
      icon: Users,
      value: "12'838 CHF",
      label: "en moyenne par citoyen (25-65 ans)",
      color: "from-green-400 to-green-600"
    },
    {
      icon: Building,
      value: "1'344'676",
      label: "comptes à la Fondation Supplétive",
      color: "from-purple-400 to-purple-600"
    },
    {
      icon: FileText,
      value: "31%",
      label: "des Suisses ont un avoir LPP",
      color: "from-orange-400 to-orange-600"
    }
  ];

  const processSteps = [
    {
      number: "1",
      title: "Formulaire Complet",
      description: "Informations personnelles sécurisées",
      icon: FileText
    },
    {
      number: "2", 
      title: "Signature Numérique",
      description: "Procuration officielle ATOM Group SA",
      icon: Shield
    },
    {
      number: "3",
      title: "Envoi Automatique",
      description: "Transmission à la Centrale du 2ème pilier (SFBVG)",
      icon: Mail
    },
    {
      number: "4",
      title: "Suivi & Résultats",
      description: "Réception par email avec suivi personnalisé",
      icon: CheckCircle
    }
  ];

  const motifs = [
    "Changement d'employeur",
    "Période de chômage", 
    "Congé sabbatique",
    "Planification retraite",
    "Fonds propres immobilier",
    "Divorce (partage des avoirs)",
    "Départ de Suisse",
    "Simple vérification"
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setFormData(prev => ({ ...prev, signatureImage: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Capturer la signature avant validation
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      const signatureData = signatureRef.current.toDataURL('image/png');
      setFormData(prev => ({ ...prev, signatureImage: signatureData }));
    }
    
    // Validation consentement RGPD (PRIORITAIRE)
    if (!formData.consentRGPD) {
      setError('Vous devez accepter le traitement de vos données personnelles pour continuer (y compris numéro AVS)');
      return;
    }
    
    // Validation des champs requis
    if (!formData.signature || !formData.acceptTerms) {
      setError('Veuillez accepter les conditions et signer électroniquement');
      return;
    }
    
    if (!formData.signatureLocation || !formData.signatureDate) {
      setError('Veuillez indiquer le lieu et la date de signature');
      return;
    }
    
    // Vérifier que la signature est dessinée
    if (signatureRef.current && signatureRef.current.isEmpty()) {
      setError('Veuillez dessiner votre signature dans le cadre prévu');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      // Capturer la signature finale
      const signatureData = signatureRef.current ? signatureRef.current.toDataURL('image/png') : '';
      const dataToSend = { ...formData, signatureImage: signatureData };
      
      const response = await axios.post(`${API}/avoirs-lpp`, dataToSend);
      
      // Vérifier le succès (soit response.data.success soit un status 200)
      if (response.status === 200 || response.data.success) {
        setIsSubmitted(true);
        setIsLoading(false);
        // Scroll vers le haut pour afficher la confirmation
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      } else {
        setError('Erreur lors de l\'envoi. Veuillez réessayer.');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Erreur envoi avoirs LPP:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi. Veuillez réessayer.');
      setIsLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 2) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const renderFormStep = () => {
    switch(currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold heading-display text-white mb-8 text-center">Étape 1 : Informations personnelles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-blue-300 mb-3">Prénom *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm"
                  placeholder="Votre prénom"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-blue-300 mb-3">Nom *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm"
                  placeholder="Votre nom"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-blue-300 mb-3">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm"
                  placeholder="votre@email.ch"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-blue-300 mb-3">Téléphone *</label>
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleInputChange}
                  className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm"
                  placeholder="+41 79 123 45 67"
                  required
                />
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="block text-sm font-medium text-blue-300 mb-3">Conseiller / Recommandant (optionnel)</label>
                <input
                  type="text"
                  name="conseiller_recommandant"
                  value={formData.conseiller_recommandant}
                  onChange={handleInputChange}
                  className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm"
                  placeholder="Nom du conseiller ou de la personne qui vous a recommandé"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-blue-300 mb-3">Date de naissance *</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-blue-300 mb-3">Genre *</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm"
                  required
                >
                  <option value="" className="bg-gray-800">Sélectionner</option>
                  <option value="homme" className="bg-gray-800">Homme</option>
                  <option value="femme" className="bg-gray-800">Femme</option>
                  <option value="autre" className="bg-gray-800">Autre</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-8">
            <h3 className="text-2xl font-bold heading-display text-white mb-8 text-center">Étape 2 : Adresse et informations complémentaires</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="md:col-span-2 space-y-2">
                <label className="block text-sm font-medium text-blue-300 mb-3">Adresse complète *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm"
                  placeholder="Rue et numéro"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-blue-300 mb-3">Code postal *</label>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm"
                  placeholder="2000"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-blue-300 mb-3">Ville *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm"
                  placeholder="Neuchâtel"
                  required
                />
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="block text-sm font-medium text-blue-300 mb-3">Numéro AVS</label>
                <input
                  type="text"
                  name="avsNumber"
                  value={formData.avsNumber}
                  onChange={handleInputChange}
                  className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm"
                  placeholder="756.XXXX.XXXX.XX"
                />
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="block text-sm font-medium text-blue-300 mb-3">Anciens employeurs (optionnel)</label>
                <textarea
                  name="formerEmployers"
                  value={formData.formerEmployers}
                  onChange={handleInputChange}
                  className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm h-28 resize-none"
                  placeholder="Listez vos anciens employeurs si vous vous en souvenez"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8">
            <h3 className="text-2xl font-bold heading-display text-white mb-8 text-center">Étape 3 : Motif et signature</h3>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-blue-300 mb-3">Motif de la recherche *</label>
              <select
                name="motif"
                value={formData.motif}
                onChange={handleInputChange}
                className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm"
                required
              >
                <option value="" className="bg-gray-800">Sélectionner un motif</option>
                {motifs.map((motif, index) => (
                  <option key={index} value={motif} className="bg-gray-800">{motif}</option>
                ))}
              </select>
            </div>
            
            {/* Lieu et Date de Signature */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-blue-300 mb-3">Lieu de signature *</label>
                <input
                  type="text"
                  name="signatureLocation"
                  value={formData.signatureLocation}
                  onChange={handleInputChange}
                  className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm"
                  placeholder="Ex: Neuchâtel"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-blue-300 mb-3">Date de signature *</label>
                <input
                  type="date"
                  name="signatureDate"
                  value={formData.signatureDate}
                  onChange={handleInputChange}
                  className="w-full p-4 bg-white border-2 border-gray-300 rounded-xl text-gray-900 font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm"
                  required
                />
              </div>
            </div>
            
            {/* Canvas de Signature */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-blue-300 mb-2">
                Votre signature manuscrite *
              </label>
              <div className="glass p-4 rounded-2xl border border-white/10 backdrop-blur-sm">
                <div className="bg-white rounded-xl overflow-hidden border-2 border-gray-300">
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      className: 'signature-canvas',
                      style: { width: '100%', height: '200px' }
                    }}
                    backgroundColor="white"
                    penColor="black"
                  />
                </div>
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="px-4 py-2 text-sm bg-red-500/20 text-red-300 rounded-lg hover:bg-red-500/30 transition-colors"
                  >
                    Effacer la signature
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Dessinez votre signature avec la souris ou le doigt (sur mobile)
                </p>
              </div>
            </div>
            
            <div className="space-y-6">
              {/* Consentement RGPD - CRITIQUE pour données sensibles */}
              <div className="glass p-6 rounded-2xl border-2 border-blue-400/50 bg-blue-500/10 backdrop-blur-sm">
                <div className="flex items-start space-x-4">
                  <input
                    type="checkbox"
                    name="consentRGPD"
                    checked={formData.consentRGPD}
                    onChange={handleInputChange}
                    className="mt-2 w-5 h-5 text-blue-600 border-2 border-blue-400 rounded focus:ring-blue-500 focus:ring-2"
                    required
                  />
                  <div className="flex-1">
                    <label className="text-sm text-gray-200 leading-relaxed">
                      <span className="font-bold text-white block mb-2">🔒 Consentement obligatoire - Protection de vos données</span>
                      J'autorise explicitement <span className="font-semibold text-white">ATOM Group SA</span> à collecter 
                      et traiter mes données personnelles sensibles (y compris mon <span className="font-semibold text-white">numéro AVS</span>, 
                      date de naissance, employeurs précédents et signature électronique) pour effectuer la recherche 
                      de mes avoirs LPP.
                      <br/><br/>
                      Je comprends que ces données seront traitées de manière <span className="font-semibold text-white">confidentielle 
                      et sécurisée</span>, conformément au <span className="font-semibold text-white">RGPD</span> et à la{' '}
                      <span className="font-semibold text-white">LPD suisse</span>.
                      <span className="text-red-400 ml-1">*</span>
                    </label>
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <p className="text-xs text-gray-400 flex items-start">
                        <Shield size={14} className="mr-2 mt-0.5 text-blue-400 flex-shrink-0" />
                        <span>
                          <strong className="text-blue-300">Vos garanties :</strong> Vos données sont chiffrées, 
                          stockées en Suisse, et conservées 10 ans (obligation légale). 
                          Vous pouvez exercer vos droits (accès, rectification, effacement) à tout moment.
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="glass p-6 rounded-2xl border border-white/10 backdrop-blur-sm">
                <div className="flex items-start space-x-4">
                  <input
                    type="checkbox"
                    name="signature"
                    checked={formData.signature}
                    onChange={handleInputChange}
                    className="mt-2 w-5 h-5 text-blue-600 border-2 border-white/20 rounded focus:ring-blue-500 focus:ring-2"
                    required
                  />
                  <label className="text-sm text-gray-300 leading-relaxed">
                    Je donne procuration à <span className="font-semibold text-white">ATOM Group SA</span> pour effectuer cette recherche en mon nom auprès de la Centrale du 2ème pilier (SFBVG) *
                  </label>
                </div>
              </div>
              
              <div className="glass p-6 rounded-2xl border border-white/10 backdrop-blur-sm">
                <div className="flex items-start space-x-4">
                  <input
                    type="checkbox"
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleInputChange}
                    className="mt-2 w-5 h-5 text-blue-600 border-2 border-white/20 rounded focus:ring-blue-500 focus:ring-2"
                    required
                  />
                  <label className="text-sm text-gray-300 leading-relaxed">
                    J'accepte les <span className="font-semibold text-white">conditions générales</span> et la <span className="font-semibold text-white">politique de confidentialité</span> *
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Page de succès
  if (isSubmitted) {
    return (
      <section ref={sectionRef} className="py-20">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <div className="glass p-12 rounded-3xl">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
                <CheckCircle size={40} className="text-white" />
              </div>
              <h2 className="text-4xl font-bold heading-display text-white mb-6">
                Demande envoyée avec succès !
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Merci {formData.firstName} {formData.lastName} ! Nous avons bien reçu votre demande de recherche d'avoirs LPP.
              </p>
              <p className="text-lg text-blue-300 mb-8">
                Un accusé de réception a été envoyé à l'adresse : <strong>{formData.email}</strong>
              </p>
              <div className="glass p-6 rounded-2xl">
                <h3 className="text-lg font-semibold text-white mb-4">Prochaines étapes :</h3>
                <ul className="text-left text-gray-300 space-y-2">
                  <li>• Traitement de votre demande sous 3-5 jours ouvrables</li>
                  <li>• Recherche approfondie dans les bases de données LPP</li>
                  <li>• Contact des institutions de prévoyance concernées</li>
                  <li>• Rapport détaillé avec les résultats de la recherche</li>
                </ul>
              </div>
              <div className="mt-8">
                <button 
                  onClick={() => {
                    setIsSubmitted(false);
                    setCurrentStep(0);
                    setError('');
                    setFormData({
                      firstName: '',
                      lastName: '',
                      email: '',
                      telephone: '',
                      conseiller_recommandant: '',
                      dateOfBirth: '',
                      gender: '',
                      address: '',
                      postalCode: '',
                      city: '',
                      avsNumber: '',
                      formerEmployers: '',
                      motif: '',
                      signatureLocation: '',
                      signatureDate: new Date().toISOString().split('T')[0],
                      signatureImage: '',
                      signature: false,
                      acceptTerms: false,
                      consentRGPD: false
                    });
                    if (signatureRef.current) {
                      signatureRef.current.clear();
                    }
                  }}
                  className="btn-glass btn-primary px-8 py-3"
                >
                  Nouvelle demande
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="py-20">
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold heading-display text-white mb-6">
            Retrouver mes avoirs LPP
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12">
            Service 100% digital pour retrouver vos avoirs de prévoyance professionnelle oubliés
          </p>
          
          {/* Statistiques LPP */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {statistics.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="glass p-6 rounded-2xl">
                  <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                    <IconComponent size={24} className="text-white" />
                  </div>
                  <div className="text-2xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-sm text-gray-300">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Processus 100% Digital */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold heading-display text-white text-center mb-12">
            🎯 Processus 100% Digital en 4 Étapes
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div key={index} className="glass p-8 text-center group hover:scale-105 transition-transform">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-2xl font-bold text-white">{step.number}</span>
                  </div>
                  <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <IconComponent size={20} className="text-purple-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-3">{step.title}</h4>
                  <p className="text-gray-300 text-sm">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Formulaire multi-étapes */}
        <div className="max-w-4xl mx-auto">
          <div className="glass p-10 rounded-3xl backdrop-blur-xl border border-white/10 shadow-2xl">
            <div className="mb-10">
              <div className="flex justify-between items-center mb-8">
                {[0, 1, 2].map((step) => (
                  <div key={step} className={`flex items-center ${step < 2 ? 'flex-1' : ''}`}>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-300 ${
                      currentStep >= step 
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg scale-110' 
                        : 'bg-white/10 text-gray-400 backdrop-blur-sm'
                    }`}>
                      {step + 1}
                    </div>
                    {step < 2 && (
                      <div className={`flex-1 h-2 mx-6 rounded-full transition-all duration-500 ${
                        currentStep > step 
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600' 
                          : 'bg-white/10'
                      }`}></div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Labels des étapes */}
              <div className="flex justify-between text-center mb-8">
                <div className={`flex-1 transition-all duration-300 ${currentStep >= 0 ? 'text-blue-300' : 'text-gray-500'}`}>
                  <p className="text-sm font-medium">Informations personnelles</p>
                </div>
                <div className={`flex-1 transition-all duration-300 ${currentStep >= 1 ? 'text-blue-300' : 'text-gray-500'}`}>
                  <p className="text-sm font-medium">Détails complémentaires</p>
                </div>
                <div className={`flex-1 transition-all duration-300 ${currentStep >= 2 ? 'text-blue-300' : 'text-gray-500'}`}>
                  <p className="text-sm font-medium">Confirmation</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              {error && (
                <div className="glass p-4 rounded-xl border border-red-500/50 bg-red-500/10 mb-6">
                  <p className="text-red-300 text-sm flex items-center">
                    <AlertCircle className="mr-2" size={16} />
                    {error}
                  </p>
                </div>
              )}
              
              {renderFormStep()}
              
              <div className="flex justify-between items-center mt-10 pt-8 border-t border-white/10">
                {currentStep > 0 && (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="btn-glass px-8 py-4 flex items-center text-lg font-medium hover:scale-105 transition-transform duration-300"
                  >
                    <ArrowRight className="mr-2 rotate-180" size={20} />
                    Précédent
                  </button>
                )}
                
                <div className={currentStep === 0 ? "ml-auto" : ""}>
                  {currentStep < 2 ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      className="btn-glass btn-primary px-8 py-4 flex items-center text-lg font-medium hover:scale-105 transition-transform duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                      disabled={currentStep === 0 && (!formData.firstName || !formData.lastName || !formData.email || !formData.telephone)}
                    >
                      Suivant
                      <ArrowRight className="ml-2" size={20} />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="btn-glass btn-primary px-10 py-4 flex items-center text-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 transition-all duration-300 disabled:hover:scale-100 shadow-2xl"
                      disabled={!formData.signature || !formData.acceptTerms || isLoading}
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Mail className="mr-3" size={20} />
                          Envoyer la demande
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Informations légales */}
        <div className="text-center mt-12">
          <div className="glass p-6 rounded-2xl max-w-3xl mx-auto">
            <div className="flex items-start space-x-4">
              <AlertCircle className="text-blue-400 mt-1 flex-shrink-0" size={20} />
              <div className="text-left">
                <h4 className="text-white font-semibold mb-2">Service gratuit et confidentiel</h4>
                <p className="text-gray-300 text-sm leading-relaxed">
                  La recherche d'avoirs LPP est un service gratuit. Vos données sont traitées de manière confidentielle 
                  et utilisées uniquement pour cette recherche. La Centrale du 2ème pilier (SFBVG) vous contactera 
                  directement si des avoirs sont trouvés.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AvoirsLPP;